import type { Primitive, SceneDoc, Source } from './schema'

/** A scene mutation that can be applied and reverted against a plain, mutable scene document. */
export interface Command {
  label: string
  redo(doc: SceneDoc): void
  undo(doc: SceneDoc): void
  /** Commands with the same key issued in quick succession collapse into one history entry. */
  key?: string
  /** Returns the command representing `this` followed by `next`. */
  merge?(next: Command): Command
}

export type Selector =
  | { c: 'root' }
  | { c: 'enclosure' }
  | { c: 'listener' }
  | { c: 'settings' }
  | { c: 'objects'; id: string }
  | { c: 'sources'; id: string }

type Patch = Record<string, unknown>

const clone = <T>(v: T): T => (v === undefined ? v : (JSON.parse(JSON.stringify(v)) as T))

function resolve(doc: SceneDoc, sel: Selector): Patch | undefined {
  switch (sel.c) {
    case 'root':
      return doc as unknown as Patch
    case 'enclosure':
      return doc.enclosure as unknown as Patch
    case 'listener':
      return doc.listener as unknown as Patch
    case 'settings':
      return doc.settings as unknown as Patch
    case 'objects':
      return doc.objects.find((o) => o.id === sel.id) as unknown as Patch | undefined
    case 'sources':
      return doc.sources.find((o) => o.id === sel.id) as unknown as Patch | undefined
  }
}

function apply(doc: SceneDoc, sel: Selector, patch: Patch) {
  const target = resolve(doc, sel)
  if (!target) return
  for (const k of Object.keys(patch)) target[k] = clone(patch[k])
}

/** Snapshot the current values of the patched keys (used to build the `before` side of a patch). */
export function snapshot(doc: SceneDoc, sel: Selector, keys: string[]): Patch {
  const target = resolve(doc, sel)
  const out: Patch = {}
  if (!target) return out
  for (const k of keys) out[k] = clone(target[k])
  return out
}

export function patchCommand(label: string, sel: Selector, before: Patch, after: Patch, key?: string): Command {
  const cmd: Command = {
    label,
    key,
    redo: (d) => apply(d, sel, after),
    undo: (d) => apply(d, sel, before),
    merge: (next) => {
      const n = next as Command & { _after?: Patch }
      const merged = patchCommand(label, sel, before, { ...after, ...(n._after ?? {}) }, key)
      return merged
    },
  }
  ;(cmd as Command & { _after?: Patch })._after = after
  return cmd
}

export function addCommand(collection: 'objects' | 'sources', item: Primitive | Source, index?: number): Command {
  const stored = clone(item)
  return {
    label: `Add ${stored.name}`,
    redo: (d) => {
      const list = d[collection] as { id: string }[]
      if (list.some((x) => x.id === stored.id)) return
      list.splice(index ?? list.length, 0, clone(stored) as never)
    },
    undo: (d) => {
      const list = d[collection] as { id: string }[]
      const i = list.findIndex((x) => x.id === stored.id)
      if (i >= 0) list.splice(i, 1)
    },
  }
}

export function removeCommand(doc: SceneDoc, collection: 'objects' | 'sources', id: string): Command | null {
  const list = doc[collection] as { id: string; name: string }[]
  const index = list.findIndex((x) => x.id === id)
  if (index < 0) return null
  const stored = clone(list[index]!)
  return {
    label: `Delete ${stored.name}`,
    redo: (d) => {
      const l = d[collection] as { id: string }[]
      const i = l.findIndex((x) => x.id === id)
      if (i >= 0) l.splice(i, 1)
    },
    undo: (d) => {
      const l = d[collection] as { id: string }[]
      if (!l.some((x) => x.id === id)) l.splice(index, 0, clone(stored))
    },
  }
}

/** Replace several things in one undo step. */
export function batchCommand(label: string, cmds: Command[]): Command {
  return {
    label,
    redo: (d) => {
      for (const c of cmds) c.redo(d)
    },
    undo: (d) => {
      for (const c of [...cmds].reverse()) c.undo(d)
    },
  }
}

export interface HistoryOptions {
  limit?: number
  coalesceMs?: number
  now?: () => number
}

export class History {
  private undoStack: Command[] = []
  private redoStack: Command[] = []
  private lastKeyTime = 0
  private limit: number
  private coalesceMs: number
  private now: () => number

  constructor(
    private mutate: (fn: (doc: SceneDoc) => void) => void,
    private onChange: () => void = () => {},
    opts: HistoryOptions = {},
  ) {
    this.limit = opts.limit ?? 200
    this.coalesceMs = opts.coalesceMs ?? 700
    this.now = opts.now ?? (() => Date.now())
  }

  get canUndo() {
    return this.undoStack.length > 0
  }
  get canRedo() {
    return this.redoStack.length > 0
  }
  get undoLabel() {
    return this.undoStack.at(-1)?.label
  }
  get redoLabel() {
    return this.redoStack.at(-1)?.label
  }

  /** Apply a command and record it. */
  exec(cmd: Command) {
    this.mutate((d) => cmd.redo(d))
    this.record(cmd)
  }

  /** Record a command whose effect has already been applied (e.g. a finished gizmo drag). */
  record(cmd: Command) {
    const t = this.now()
    const last = this.undoStack.at(-1)
    if (last && cmd.key && last.key === cmd.key && last.merge && t - this.lastKeyTime < this.coalesceMs) {
      this.undoStack[this.undoStack.length - 1] = last.merge(cmd)
    } else {
      this.undoStack.push(cmd)
      if (this.undoStack.length > this.limit) this.undoStack.shift()
    }
    this.lastKeyTime = t
    this.redoStack = []
    this.onChange()
  }

  undo() {
    const cmd = this.undoStack.pop()
    if (!cmd) return
    this.mutate((d) => cmd.undo(d))
    this.redoStack.push(cmd)
    this.lastKeyTime = 0
    this.onChange()
  }

  redo() {
    const cmd = this.redoStack.pop()
    if (!cmd) return
    this.mutate((d) => cmd.redo(d))
    this.undoStack.push(cmd)
    this.lastKeyTime = 0
    this.onChange()
  }

  clear() {
    this.undoStack = []
    this.redoStack = []
    this.onChange()
  }
}
